import { useQuery } from '@apollo/client';
import { css } from '@emotion/css';
import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import ScanElseImage from '@volumental/assets/images/scanner/scannerPicker/scan_someone.png';
import ScanSelfImage from '@volumental/assets/images/scanner/scannerPicker/scan_yourself.png';
import {
  AgeRestrictionModal,
  Container,
  Content,
  Headline,
  Loader,
} from '@volumental/common';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql } from '../../api/gql';
import { Header } from '../../components/accessible-components';
import { Modal } from '../../components/modal/modal';
import { UserFlowType } from '../../types/types';
import { GeneralErrorPage } from '../error/GeneralErrorPage';
import { ErrorBox } from './components/error-box';
import { PickerButton } from './components/picker-button';

const GetPickerConfigurationQuery = graphql(`
  query getPickerConfigurationQuery {
    configuration {
      scanner {
        allowSelfScanning
        allowAssistedScanning
      }
    }
  }
`);

const titleStyle = css`
  font-size: 21px;
  margin: 16px 0;
  font-weight: normal;
`;
interface PickerViewProps {
  onClose: () => void;
  onBack?: () => void;
  onContinue: (scannerType: UserFlowType) => void;
  title: string;
}

export function PickerView({
  onClose,
  onBack,
  onContinue,
  title,
}: PickerViewProps): JSX.Element {
  const { loading, error, data } = useQuery(GetPickerConfigurationQuery);
  const [showAgeRestriction, setShowAgeRestriction] = useState(false);
  const { t } = useTranslation();

  if (loading) return <Loader />;
  if (error || !data) {
    return (
      <GeneralErrorPage
        errorText="Missing PickerView configuration"
        onClose={onClose}
      />
    );
  }

  const {
    scanner: { allowAssistedScanning, allowSelfScanning },
  } = data.configuration;

  const handleSoloContinue = (): void => {
    onContinue(UserFlowType.Solo);
  };

  const handleAssistedContinue = (): void => {
    usageTracker.track(UsageTrackerEvent.AgeRestriction, {
      'user choise': 'confirm',
    });
    setShowAgeRestriction(false);
    onContinue(UserFlowType.Assisted);
  };

  return (
    <Container>
      <Header back={onBack} close={onClose} />
      <Content disableScrollbar>
        <Headline className={titleStyle}>{title}</Headline>

        {allowSelfScanning ? (
          <PickerButton
            onClick={handleSoloContinue}
            imageSrc={ScanSelfImage}
            title={t('multiIntro.selfScanning')}
            description={t('multiIntro.scanYourself')}
            altText={t('imageAlt.pickerSolo')}
          />
        ) : undefined}
        {allowAssistedScanning ? (
          <PickerButton
            onClick={() => {
              setShowAgeRestriction(true);
            }}
            imageSrc={ScanElseImage}
            title={t('multiIntro.assistedScanning')}
            description={t('multiIntro.scanSomeone')}
            altText={t('imageAlt.pickerAssisted')}
          />
        ) : undefined}
        {!allowSelfScanning && !allowAssistedScanning && <ErrorBox />}
      </Content>
      <Modal
        modalTitle={t('modalTitles.ageRestriction')}
        show={showAgeRestriction}
        close={() => {
          setShowAgeRestriction(false);
        }}
        fitContent
      >
        <AgeRestrictionModal
          proceed={handleAssistedContinue}
          title={t('ageRestriction.title')}
          description={t('ageRestriction.description')}
          acceptText={t('button.confirm')}
        />
      </Modal>
    </Container>
  );
}
